import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlaylistItem, Version } from '../types.ts';
import { compareVersions, naturalCompare } from '../naming.ts';

/** One entry in the shot strip: a shot, with every version of it that the playlist carries. */
export interface ClipGroup {
  shotId: string;
  shotName: string;
  /** Ascending by version number, so the last one is the highest. */
  versions: PlaylistItem[];
}

export interface ClipGroups {
  groups: ClipGroup[];
  currentIndex: number;
  currentGroup: ClipGroup | null;
  /** The version shown for the current clip. */
  version: Version | null;
  /** One representative item per clip (the shown version), for the shot strip. */
  stripItems: PlaylistItem[];
  /**
   * Moves to `index` (clamped) and reports where that landed, so the caller can tell the room
   * about it. Does not touch playback — resetting the player is the caller's business.
   */
  select: (index: number) => { index: number; versionId: string | null };
  /** Show a different version of a shot. */
  show: (shotId: string, versionId: string) => void;
  /**
   * Follow the room: show `versionId`, moving to its clip if that is a different one.
   * Returns false if this playlist does not contain that version.
   */
  follow: (versionId: string) => boolean;
  /**
   * The live index, written synchronously by `select`. Without it, two quick clicks on
   * "next shot" land in the same React batch, both read the same stale `currentIndex` from
   * their render closure, and the playlist advances only one step.
   */
  currentIndexRef: React.RefObject<number>;
}

const highestOf = (group: ClipGroup): string | undefined =>
  group.versions[group.versions.length - 1]?.version.id;

/**
 * Turns the flat playlist into clips — one entry per shot, with the versions switchable —
 * and remembers which version each shot is showing.
 *
 * `sortByName` decides the clip order: true (the default) sorts shots alphanumerically, so a
 * playlist reads in a predictable order regardless of how it was assembled; false keeps the
 * incoming `items` order, which is what a manually reordered playlist wants.
 */
export function useClipGroups(items: PlaylistItem[], sortByName = true): ClipGroups {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  // Which version of each shot is shown; defaults to the highest, switchable in the header.
  const [chosenByShot, setChosenByShot] = useState<Record<string, string>>({});

  // Versions are sorted ascending here so "last = highest" holds no matter what order the
  // playlist was built in (an OTIO import does not have to be in version order).
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, PlaylistItem[]>();
    for (const it of items) {
      const arr = map.get(it.version.shotId);
      if (arr) arr.push(it);
      else {
        map.set(it.version.shotId, [it]);
        order.push(it.version.shotId);
      }
    }
    const built = order.map((shotId) => {
      const groupItems = (map.get(shotId) ?? []).sort((a, b) => compareVersions(a.version, b.version));
      return { shotId, shotName: groupItems[0]?.version.shotName ?? shotId, versions: groupItems };
    });
    // Default order is by shot name; a manual order lives in the items array itself.
    return sortByName ? [...built].sort((a, b) => naturalCompare(a.shotName, b.shotName)) : built;
  }, [items, sortByName]);

  // Default each shot's shown version to its highest; keep a still-valid manual choice.
  useEffect(() => {
    setChosenByShot((prev) => {
      const next: Record<string, string> = {};
      for (const g of groups) {
        const keep = prev[g.shotId];
        next[g.shotId] = keep && g.versions.some((v) => v.version.id === keep) ? keep : (highestOf(g) ?? '');
      }
      return next;
    });
  }, [groups]);

  const currentGroup = groups[currentIndex] ?? null;

  const version = useMemo(() => {
    if (!currentGroup) return null;
    const chosen = chosenByShot[currentGroup.shotId];
    return (
      currentGroup.versions.find((v) => v.version.id === chosen)?.version ??
      currentGroup.versions[currentGroup.versions.length - 1]?.version ??
      null
    );
  }, [currentGroup, chosenByShot]);

  const stripItems = useMemo(
    () =>
      groups.map(
        (g) =>
          g.versions.find((v) => v.version.id === chosenByShot[g.shotId]) ??
          g.versions[g.versions.length - 1]!,
      ),
    [groups, chosenByShot],
  );

  /*
   * The callbacks below are handed to effects that must not be torn down on every render — the
   * sync heartbeat runs twice a second. So they read the live playlist through refs and stay
   * referentially stable for the lifetime of the component.
   */
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const chosenRef = useRef(chosenByShot);
  chosenRef.current = chosenByShot;

  const select = useCallback((index: number) => {
    const all = groupsRef.current;
    const clamped = Math.max(0, Math.min(all.length - 1, index));
    currentIndexRef.current = clamped;
    setCurrentIndex(clamped);
    const group = all[clamped];
    const versionId = (group && (chosenRef.current[group.shotId] ?? highestOf(group))) ?? null;
    return { index: clamped, versionId };
  }, []);

  const show = useCallback((shotId: string, versionId: string) => {
    setChosenByShot((prev) => (prev[shotId] === versionId ? prev : { ...prev, [shotId]: versionId }));
  }, []);

  const follow = useCallback(
    (versionId: string) => {
      const index = groupsRef.current.findIndex((g) =>
        g.versions.some((v) => v.version.id === versionId),
      );
      if (index < 0) return false;
      if (index !== currentIndexRef.current) select(index);
      show(groupsRef.current[index]!.shotId, versionId);
      return true;
    },
    [select, show],
  );

  return {
    groups,
    currentIndex,
    currentGroup,
    version,
    stripItems,
    select,
    show,
    follow,
    currentIndexRef,
  };
}
